import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT, LocalStorageKeys, LocalStorageService } from '@iot-platform/core';
import { Environment } from '@iot-platform/models/common';
import { SidenavMenuBlock } from '@iot-platform/models/i4b';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { bypassHttpContextBusinessProfileIdHeader } from '../auth.interceptor';

import { AuthorizationConcept, AuthorizationType, Privileges } from '../authorization.types';

/** Body of `GET /account/privileges`. */
interface PrivilegesResponse {
  concepts: Privileges;
}

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  protected environment: Environment = inject(ENVIRONMENT);
  protected http: HttpClient = inject(HttpClient);
  protected storage: LocalStorageService = inject(LocalStorageService);

  public getAuthorizations(): Observable<Privileges> {
    return this.http.get<PrivilegesResponse>(`${this.environment.api.url}/account/privileges`).pipe(
      tap((privileges) => this.storage.set(LocalStorageKeys.STORAGE_PRIVILEGES_KEY, JSON.stringify(privileges.concepts))),
      map((response) => response.concepts)
    );
  }

  public getAuthorizationsByBusinessProfile(businessProfileId: string): Observable<Privileges> {
    const headers = new HttpHeaders().set('business-profile-id', businessProfileId);
    return this.http
      .get<PrivilegesResponse>(`${this.environment.api.url}/account/privileges`, {
        headers,
        context: bypassHttpContextBusinessProfileIdHeader()
      })
      .pipe(map((response) => response.concepts));
  }

  applyAuthorization(concept: AuthorizationConcept, authorizationType: AuthorizationType): boolean {
    const authz = JSON.parse(this.storage.get(LocalStorageKeys.STORAGE_PRIVILEGES_KEY));
    if (authz && authz[concept?.toUpperCase()]) {
      return authz[concept.toUpperCase()].includes(authorizationType.toLowerCase());
    }
    return false;
  }

  getNavigationTree(navTree: SidenavMenuBlock[]): SidenavMenuBlock[] {
    navTree.forEach((block) => {
      block.display = false;
      block.items.forEach((item) => {
        if (item.concepts) {
          item.display = this.processConditionsOnConcepts(item.concepts);
          if (item.display) {
            block.display = true;
          }
          if (item.submenu) {
            item.submenu = this.getNavigationTree(item.submenu);
          }
        } else {
          item.display = true;
          block.display = true;
        }
      });
    });

    return navTree;
  }

  processConditionsOnConcepts(concepts: { name: string; right: string }[][]): boolean {
    if (!concepts || (concepts && concepts.length === 0)) {
      return true;
    } else {
      return concepts.reduce((andConditionArrayResult, orConditionArray) => {
        const orConditionResult = orConditionArray.reduce((conditionResult, condition) => {
          conditionResult = conditionResult || this.applyAuthorization(AuthorizationConcept[condition.name], AuthorizationType[condition.right]);
          return conditionResult;
        }, false);

        return andConditionArrayResult && orConditionResult;
      }, true);
    }
  }
}
